import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService } from '../../services/notificationService';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

import { Plus, Save, Eye, Code, Edit2, FileText, Mail, DollarSign } from 'lucide-react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import TemplateList from './components/TemplateList';
import DraggableBlock from './components/DraggableBlock';
import EditorCanvas from './components/EditorCanvas';
import VariableInserter from './components/VariableInserter';
import TemplatePreview from './components/TemplatePreview';

export default function NotificationTemplateManagement() {
  const { user, profile } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorBlocks, setEditorBlocks] = useState([]);
  const [activeView, setActiveView] = useState('editor');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'general',
    subject: '',
    variables: []
  });
  const [previewVariables, setPreviewVariables] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const availableBlocks = [
    { id: 'header', type: 'header', icon: FileText, label: 'Header', content: '<h2>Header Text</h2>' },
    { id: 'text', type: 'text', icon: FileText, label: 'Text Block', content: '<p>Your text here...</p>' },
    { id: 'button', type: 'button', icon: Mail, label: 'Button', content: '<a href="#" style="display:inline-block;padding:12px 24px;background:#4F46E5;color:white;text-decoration:none;border-radius:6px;">Click Here</a>' },
    { id: 'divider', type: 'divider', icon: DollarSign, label: 'Divider', content: '<hr style="border:1px solid #E5E7EB;margin:20px 0;" />' }
  ];

  const categoryOptions = [
    { value: 'appointment', label: 'Appointment' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'general', label: 'General' }
  ];

  useEffect(() => {
    loadTemplates();
  }, [profile]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      if (profile?.tenantId) {
        const data = await notificationService?.getTemplates(profile?.tenantId);
        setTemplates(data || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template?.name,
      category: template?.category,
      subject: template?.subject,
      variables: template?.variables || []
    });
    
    parseHtmlToBlocks(template?.bodyHtml);
    
    const vars = {};
    (template?.variables || [])?.forEach(v => {
      vars[v] = `{{${v}}}`;
    });
    setPreviewVariables(vars);
  };

  const parseHtmlToBlocks = (html) => {
    const parser = new DOMParser();
    const doc = parser?.parseFromString(html, 'text/html');
    const blocks = [];
    let blockId = 0;

    doc?.body?.childNodes?.forEach(node => {
      if (node?.nodeType === Node.ELEMENT_NODE) {
        const tagName = node?.tagName?.toLowerCase();
        let type = 'text';
        
        if (tagName === 'h1' || tagName === 'h2' || tagName === 'h3') {
          type = 'header';
        } else if (tagName === 'a' && node?.style?.display === 'inline-block') {
          type = 'button';
        } else if (tagName === 'hr') {
          type = 'divider';
        }

        blocks?.push({
          id: `block-${blockId++}`,
          type,
          content: node?.outerHTML
        });
      }
    });

    setEditorBlocks(blocks);
  };

  const handleDragStart = (event) => {
    setActiveId(event?.active?.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      return;
    }

    if (active?.id?.startsWith('available-')) {
      const blockType = active?.id?.replace('available-', '');
      const block = availableBlocks?.find(b => b?.id === blockType);
      
      if (block) {
        const newBlock = {
          id: `block-${Date.now()}`,
          type: block?.type,
          content: block?.content
        };
        setEditorBlocks([...editorBlocks, newBlock]);
      }
    } else if (over && active?.id !== over?.id) {
      const oldIndex = editorBlocks?.findIndex(b => b?.id === active?.id);
      const newIndex = editorBlocks?.findIndex(b => b?.id === over?.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newBlocks = [...editorBlocks];
        const [removed] = newBlocks?.splice(oldIndex, 1);
        newBlocks?.splice(newIndex, 0, removed);
        setEditorBlocks(newBlocks);
      }
    }

    setActiveId(null);
  };

  const handleBlockUpdate = (blockId, newContent) => {
    setEditorBlocks(editorBlocks?.map(block =>
      block?.id === blockId ? { ...block, content: newContent } : block
    ));
  };

  const handleBlockDelete = (blockId) => {
    setEditorBlocks(editorBlocks?.filter(block => block?.id !== blockId));
  };

  const handleVariableInsert = (variable) => {
    if (!templateForm?.variables?.includes(variable)) {
      setTemplateForm({
        ...templateForm,
        variables: [...templateForm?.variables, variable]
      });
    }
  };

  const generateHtmlFromBlocks = () => {
    return editorBlocks?.map(block => block?.content)?.join('\n');
  };

  const generateTextFromHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser?.parseFromString(html, 'text/html');
    return doc?.body?.textContent || '';
  };

  const handleSaveTemplate = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const bodyHtml = generateHtmlFromBlocks();
      const bodyText = generateTextFromHtml(bodyHtml);

      if (selectedTemplate) {
        await notificationService?.updateTemplate(selectedTemplate?.id, {
          name: templateForm?.name,
          subject: templateForm?.subject,
          bodyHtml,
          bodyText,
          variables: templateForm?.variables
        });
        setSuccess('Template updated successfully');
      } else {
        await notificationService?.createTemplate({
          tenantId: profile?.tenantId,
          name: templateForm?.name,
          category: templateForm?.category,
          subject: templateForm?.subject,
          bodyHtml,
          bodyText,
          isActive: true,
          variables: templateForm?.variables,
          createdBy: user?.id
        });
        setSuccess('Template created successfully');
      }

      await loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      setError('');
      await notificationService?.deleteTemplate(templateId);
      setSuccess('Template deleted successfully');
      
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setTemplateForm({
          name: '',
          category: 'general',
          subject: '',
          variables: []
        });
        setEditorBlocks([]);
      }
      
      await loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to delete template');
    }
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setTemplateForm({
      name: '',
      category: 'general',
      subject: '',
      variables: []
    });
    setEditorBlocks([]);
    setPreviewVariables({});
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notification Templates</h1>
        <Button
          onClick={handleNewTemplate}
          className="flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          New Template
        </Button>
      </div>

      <div className="flex h-screen bg-gray-50">
        <Sidebar onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          
          <main className="flex-1 overflow-hidden">
            <div className="h-full flex">
              {/* Template Library - Left Panel */}
              <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Templates</h2>
                    <Button
                      onClick={handleNewTemplate}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      New
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : (
                    <TemplateList
                      templates={templates}
                      selectedTemplate={selectedTemplate}
                      onSelect={handleTemplateSelect}
                      onDelete={handleDeleteTemplate}
                    />
                  )}
                </div>
              </div>

              {/* Editor/Preview - Center/Right Panels */}
              <div className="flex-1 flex flex-col">
                {/* Top Bar */}
                <div className="bg-white border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4 flex-1">
                      <Input
                        placeholder="Template Name"
                        value={templateForm?.name}
                        onChange={(e) => setTemplateForm({ ...templateForm, name: e?.target?.value })}
                        className="flex-1 max-w-xs"
                      />
                      <Select
                        value={templateForm?.category}
                        onChange={(e) => setTemplateForm({ ...templateForm, category: e?.target?.value })}
                        options={categoryOptions}
                        className="w-40"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => setActiveView('editor')}
                        variant={activeView === 'editor' ? 'primary' : 'secondary'}
                        className="flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Editor
                      </Button>
                      <Button
                        onClick={() => setActiveView('preview')}
                        variant={activeView === 'preview' ? 'primary' : 'secondary'}
                        className="flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Preview
                      </Button>
                      <Button
                        onClick={() => setActiveView('code')}
                        variant={activeView === 'code' ? 'primary' : 'secondary'}
                        className="flex items-center gap-2"
                      >
                        <Code className="w-4 h-4" />
                        Code
                      </Button>
                    </div>
                  </div>

                  <Input
                    placeholder="Email Subject"
                    value={templateForm?.subject}
                    onChange={(e) => setTemplateForm({ ...templateForm, subject: e?.target?.value })}
                    className="w-full"
                  />

                  {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                      {success}
                    </div>
                  )}
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-hidden flex">
                  {activeView === 'editor' && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      {/* Available Blocks */}
                      <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Content Blocks</h3>
                        <div className="space-y-2">
                          {availableBlocks?.map((block) => (
                            <DraggableBlock
                              key={`available-${block?.id}`}
                              id={`available-${block?.id}`}
                              block={block}
                              isAvailable
                            />
                          ))}
                        </div>

                        <div className="mt-6">
                          <VariableInserter
                            variables={templateForm?.variables}
                            onInsert={handleVariableInsert}
                          />
                        </div>
                      </div>

                      {/* Editor Canvas */}
                      <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
                        <div className="max-w-3xl mx-auto">
                          <EditorCanvas
                            blocks={editorBlocks}
                            onBlockUpdate={handleBlockUpdate}
                            onBlockDelete={handleBlockDelete}
                          />
                        </div>
                      </div>

                      <DragOverlay>
                        {activeId ? (
                          <div className="bg-white border-2 border-indigo-500 rounded-lg p-3 shadow-lg">
                            Dragging...
                          </div>
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}

                  {activeView === 'preview' && (
                    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
                      <TemplatePreview
                        subject={templateForm?.subject}
                        html={generateHtmlFromBlocks()}
                        variables={previewVariables}
                        onVariableChange={setPreviewVariables}
                      />
                    </div>
                  )}

                  {activeView === 'code' && (
                    <div className="flex-1 overflow-y-auto bg-gray-900 p-6">
                      <div className="max-w-4xl mx-auto">
                        <pre className="text-green-400 text-sm font-mono">
                          {generateHtmlFromBlocks()}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Action Bar */}
                <div className="bg-white border-t border-gray-200 p-4">
                  <div className="flex items-center justify-end gap-3">
                    <Button
                      onClick={handleSaveTemplate}
                      disabled={saving || !templateForm?.name || !templateForm?.subject}
                      className="flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save Template'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}